import { styled } from "@gluestack-style/react";
import { CloseIcon, Input, InputField, InputIcon, InputSlot, SearchIcon } from "components/ui";
import { usePathname, router } from "expo-router";
import { MotiView } from "moti";
import React, { useRef, useCallback, useState } from "react";
import { Pressable } from "react-native";
import { useGetSearchQuery } from "store/abcoApi";
import { useSiteSearch } from "utils/hooks/useSiteSearch";
import { debounce } from "utils/globalFunctions";
import { useIsMobile } from "utils/hooks/useIsMobile";

const StyledMotiView: any = styled(MotiView, {});

interface SearchBarProps {
	showMobileOverlay?: boolean;
	fluid?: boolean;
	inline?: boolean;
}

export default function SearchBar(props: SearchBarProps) {
	const currentPath = usePathname();
	const { open, close, setResults, searchTerm, setSearchTerm } = useSiteSearch();
	const [inputValue, setInputValue] = useState(searchTerm);
	let isMobile = useIsMobile();

	const { data, isLoading, isFetching, isError, error, currentData } = useGetSearchQuery({ searchTerm: searchTerm }, { skip: searchTerm.length == 0 });

	const debouncedSetSearchTerm = useCallback(
		debounce((text: string) => {
			setSearchTerm(text);
		}, 300),
		[],
	);

	const handleOpen = () => {
		if (currentPath !== "/search" && !props.inline) {
			open();
		}
	};

	const inputRef: any = useRef(null);

	React.useEffect(() => {
		if (data) {
			setResults(data);
		}
	}, [data]);

	React.useEffect(() => {
		return () => {
			debouncedSetSearchTerm.cancel();
		};
	}, [debouncedSetSearchTerm]);

	React.useEffect(() => {
		setInputValue(searchTerm);
	}, [searchTerm]);

	const handleEnter = () => {
		setSearchTerm(inputValue);
		if (inputValue.length > 0) {
			router.navigate("/search?term=" + inputValue);
			close();
		}
	};

	return (
		<>
			<Input
				flex={1}
				backgroundColor="$basicwhite"
				variant="outline"
				borderRadius={12}
				borderColor="$borderBlue"
				size="md"
				px="$0"
				py={"$3"}
				isDisabled={false}
				isInvalid={false}
				isReadOnly={false}
				style={{
					zIndex: 100,
				}}
				isFocused={true}
				isHovered={true}
			>
				<InputSlot
					onPress={() => {
						setSearchTerm(inputValue);
						handleEnter();
					}}
				>
					<InputIcon as={SearchIcon} ml="$4" color="$basicblack" />
				</InputSlot>
				<InputField
					ref={inputRef}
					fontSize={"$h5desktop"}
					placeholder="Search for..."
					value={inputValue}
					onChangeText={(text) => {
						setInputValue(text);
						if (!isMobile || currentPath === "/search") {
							debouncedSetSearchTerm(text);
						}
					}}
					onFocus={!isMobile ? handleOpen : undefined}
					// onBlur={doClose}
					color="$basicblack"
					placeholderTextColor={"#cccccc"}
					onKeyPress={(e) => {
						if (e.key === "Escape") {
							// doClose();
							close();
							if (inputRef.current) {
								inputRef.current.blur();
							}
						} else if (e.key === "Enter") {
							handleEnter();
						}
					}}
				/>
				{inputValue.length > 0 && (
					<InputSlot>
						<Pressable
							onPress={() => {
								setInputValue("");
								setSearchTerm("");
								debouncedSetSearchTerm.cancel(); // Cancel any pending debounced calls
								inputRef.current.focus();
							}}
						>
							<InputIcon as={CloseIcon} mr="$4" color="$basicblack" />
						</Pressable>
					</InputSlot>
				)}
			</Input>
		</>
	);
}
